"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useSyncExternalStore } from "react";
import { motion, useMotionTemplate, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { OctarnalLogo } from "@/assets/icons/MainPageIcon";
import { Container, SectionSubHeading, SectionTitle, CapabilityCardVisual } from "@/components";
import { capabilityNodes, CapabilityNode } from "@/data/CapabilityData";
import { fade } from "@/lib/animation/variant";
import { useConnectorLines } from "@/hooks/animations/useConnectorLines";

// Desktop only — the scroll-driven blur is the heaviest paint on mobile, and the
// connector web it complements is hidden below lg anyway, so on the phone stack
// it just janks every card for nothing.
const subscribeViewport = (cb: () => void) => {
  window.addEventListener("resize", cb);
  return () => window.removeEventListener("resize", cb);
};
const CapabilityCard = ({
  node,
  setRef
}: {
  node: CapabilityNode;
  setRef: (el: HTMLDivElement | null) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const isDesktop = useSyncExternalStore(subscribeViewport, () => window.innerWidth >= 1024, () => false);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const maxBlur = reduce || !isDesktop ? 0 : 10;
  const rawBlur = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [maxBlur, 0, 0, maxBlur]);
  const blur = useSpring(rawBlur, {
    stiffness: 120,
    damping: 24,
    restDelta: 0.01
  });
  const filter = useMotionTemplate`blur(${blur}px)`;
  return <motion.div ref={el => {
    ref.current = el;
    setRef(el);
  }} style={{
    filter
  }} className="relative z-10 flex flex-col rounded-3xl border border-card-foreground/25 bg-card-foreground p-6 transition-colors duration-300 hover:border-card-foreground/40 lg:min-h-110">
      <h3 className="text-lg font-black uppercase tracking-tight text-primary">
        {node.title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-white/80 mb-5 md:mb-8 lg:mb-0">
        {node.blurb}
      </p>
      <CapabilityCardVisual node={node} />
    </motion.div>;
};
const CapabilityWeb = () => {
  const $ = _c(25);
  const {
    stageRef,
    hubRef,
    setCardRef,
    lines,
    size
  } = useConnectorLines(capabilityNodes.length);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="pointer-events-none absolute inset-0 opacity-60" style={{
      backgroundImage: "linear-gradient(to right, rgba(0,0,0,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.035) 1px, transparent 1px)",
      backgroundSize: "80px 80px",
      maskImage: "linear-gradient(to bottom, transparent, black 18%, black 82%, transparent)",
      WebkitMaskImage: "linear-gradient(to bottom, transparent, black 18%, black 82%, transparent)"
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      once: true,
      amount: 0.5
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SectionSubHeading subtitle="Under The Hood" className="text-neutral-900" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <motion.div variants={fade} initial="hidden" whileInView="show" viewport={t1} className="mx-auto max-w-2xl space-y-2 text-center">{t2}<SectionTitle title={<>BUILT RIGHT, <br /> BUILT TO LAST.</>} className="text-neutral-900" /></motion.div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <motion.p variants={fade} initial="hidden" whileInView="show" viewport={{
      once: true,
      amount: 0.5
    }} custom={1} className="mx-auto mt-5 max-w-xl text-center text-base leading-relaxed text-neutral-500 md:text-lg">Every site we ship is fast, findable and yours to run — engineered to keep performing long after launch day.</motion.p>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = size.w;
  const t6 = size.h;
  const t7 = `0 0 ${size.w || 1} ${size.h || 1}`;
  let t8;
  if ($[5] !== lines) {
    t8 = lines.map(_temp);
    $[5] = lines;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== size.h || $[8] !== size.w || $[9] !== t7 || $[10] !== t8) {
    t9 = <svg aria-hidden={true} className="pointer-events-none absolute inset-0 z-0 hidden lg:block" width={t5} height={t6} viewBox={t7} fill="none">{t8}</svg>;
    $[7] = size.h;
    $[8] = size.w;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="absolute -z-10 size-28 rounded-full bg-card-foreground/15 blur-2xl" />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="relative grid place-items-center">{t10}<div className="grid size-20 place-items-center rounded-2xl border border-black/5 bg-card-foreground shadow-[0_0_50px_rgba(33,33,41,0.18)]"><OctarnalLogo size={40} /></div></div>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== hubRef) {
    t12 = <div ref={hubRef} className="absolute left-1/2 top-1/2 z-20 hidden -translate-x-1/2 -translate-y-1/2 lg:flex">{t11}</div>;
    $[14] = hubRef;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== setCardRef) {
    t13 = capabilityNodes.map((node, i_0) => <CapabilityCard key={node.id} node={node} setRef={setCardRef(i_0)} />);
    $[16] = setCardRef;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t13) {
    t14 = <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-72">{t13}</div>;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== stageRef || $[21] !== t12 || $[22] !== t14 || $[23] !== t9) {
    t15 = <section className="relative w-full overflow-hidden bg-white pt-20 pb-28 sm:pb-32 md:pt-24 md:pb-40 lg:py-28">{t0}<Container className="relative px-4 lg:px-10">{t3}{t4}<div ref={stageRef} className="relative mt-16 lg:mt-24">{t9}{t12}{t14}</div></Container></section>;
    $[20] = stageRef;
    $[21] = t12;
    $[22] = t14;
    $[23] = t9;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
};
export default CapabilityWeb;
function _temp(l, i) {
  return <g key={i}><path d={l.d} className="stroke-card-foreground" strokeOpacity={0.28} strokeWidth={1.5} strokeLinecap="round" /><circle r={4} className="fill-card-foreground"><animateMotion dur="2.8s" begin={`${i * 0.34}s`} repeatCount="indefinite" path={l.d} calcMode="spline" keyPoints="0;1" keyTimes="0;1" keySplines="0.1 0.85 0.25 1" /><animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.85;1" dur="2.8s" begin={`${i * 0.34}s`} repeatCount="indefinite" /></circle></g>;
}